import * as Sentry from "@sentry/react";
import { fetchAuthSession } from "aws-amplify/auth";

import useAuthStore from "../stores/authStore";

const API_URL = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  constructor(status, data, message) {
    super(message || (data && (data.detail || data.message)) || `Request failed with status ${status}.`);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

let refreshPromise = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = fetchAuthSession()
      .then(({ tokens }) => {
        if (!tokens?.idToken) {
          return null;
        }
        const session = {
          accessToken: tokens.accessToken?.toString(),
          idToken: tokens.idToken.toString(),
          idTokenPayload: tokens.idToken.payload,
        };
        useAuthStore.getState().setCredentials(session);
        return session;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request(path, options = {}) {
  const { method = "GET", body, headers: extraHeaders, signal } = options;
  const { idToken } = useAuthStore.getState();

  const doRequest = async (token) =>
    fetch(`${API_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: token } : {}),
        ...extraHeaders,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

  let response;
  try {
    response = await doRequest(idToken);
  } catch (err) {
    if (err.name === "AbortError") {
      throw err;
    }
    Sentry.captureException(err);
    throw new ApiError(0, null, "Network request failed.");
  }

  if (response.status === 401) {
    let session;
    try {
      session = await refreshSession();
    } catch (refreshError) {
      Sentry.captureException(refreshError);
      useAuthStore.getState().logOut();
      throw new ApiError(401, null, "Session refresh failed.");
    }
    if (!session) {
      useAuthStore.getState().logOut();
      throw new ApiError(401, null, "Session refresh failed.");
    }
    response = await doRequest(session.idToken);
  }

  const data = await parseResponse(response);
  if (!response.ok) {
    throw new ApiError(response.status, data);
  }
  return data;
}

export function apiFetch(path, options) {
  return request(path, options);
}
